import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, CheckCircle, XCircle, Eye, Clock, RefreshCw,
  Mail, Globe, Calendar, AlertCircle, Search,
  ShieldCheck, ShieldX, Loader2
} from 'lucide-react';
import { useKYC } from '../../hooks/useKYC';
import { useAdmin } from '../../hooks/useAdmin';

const STATUS_CONFIG = {
  pending:  { label: 'En attente',   color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/20' },
  verified: { label: 'Vérifié',      color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  rejected: { label: 'Rejeté',       color: 'text-rose-400',    bg: 'bg-rose-500/10',    border: 'border-rose-500/20' },
};

export default function KYCManagement() {
  const navigate = useNavigate();
  const { verifyKYC } = useKYC();
  const { getUsers, loading } = useAdmin();

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ pending: 0, verified: 0, rejected: 0 });
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadUsers = useCallback(async () => {
    try {
      const [pendingRes, verifiedRes, rejectedRes] = await Promise.all([
        getUsers({ kyc_status: 'pending', limit: 200 }),
        getUsers({ kyc_status: 'verified', limit: 200 }),
        getUsers({ kyc_status: 'rejected', limit: 200 }),
      ]);

      const pending  = pendingRes.success  ? (pendingRes.data?.users  || []) : [];
      const verified = verifiedRes.success ? (verifiedRes.data?.users || []) : [];
      const rejected = rejectedRes.success ? (rejectedRes.data?.users || []) : [];

      setStats({ pending: pending.length, verified: verified.length, rejected: rejected.length });

      const map = { pending, verified, rejected };
      setUsers(map[filter] || []);
    } catch (err) {
      showToast('Erreur lors du chargement', 'error');
    }
  }, [getUsers, filter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleVerify = async (userId, status) => {
    setProcessingId(userId);
    const res = await verifyKYC(userId, status);
    if (res.success) {
      showToast(`KYC ${status === 'verified' ? 'approuvé' : 'rejeté'} avec succès`);
      loadUsers();
    } else {
      showToast(res.message || 'Erreur', 'error');
    }
    setProcessingId(null);
  };

  const filtered = users.filter(u =>
    !search ||
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="admin-kyc-container relative">
      <div className="absolute inset-0 bg-indigo-500/5 blur-[150px] -z-10 pointer-events-none" />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl font-bold text-sm animate-fade-in ${
          toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {toast.msg}
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-8 animate-fade-in">
        {/* Title */}
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-500/10 border border-violet-500/20 rounded-full mb-4">
            <Shield size={12} className="text-violet-400" />
            <span className="text-[10px] font-black text-violet-400 uppercase tracking-[0.2em]">Admin / KYC</span>
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight">Gestion des <span className="text-violet-400">KYC</span></h1>
          <p className="text-slate-400 mt-1">Vérifiez et approuvez les dossiers d'identité des utilisateurs.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { key: 'pending',  label: 'En attente',   icon: Clock,         color: 'amber',   count: stats.pending  },
            { key: 'verified', label: 'Vérifiés',     icon: ShieldCheck,   color: 'emerald', count: stats.verified },
            { key: 'rejected', label: 'Rejetés',      icon: ShieldX,       color: 'rose',    count: stats.rejected },
          ].map(({ key, label, icon: Icon, color, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`glass-card p-6 border text-left transition-all ${
                filter === key
                  ? `border-${color}-500/40 bg-${color}-500/10`
                  : 'border-white/5 hover:border-white/10'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-xl bg-${color}-500/10 border border-${color}-500/20`}>
                  <Icon size={20} className={`text-${color}-400`} />
                </div>
                {filter === key && <div className={`w-2 h-2 rounded-full bg-${color}-500 animate-pulse`} />}
              </div>
              <p className={`text-3xl font-black text-${color}-400`}>{count}</p>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">{label}</p>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="glass-card border-white/5">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-6 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className={`px-3 py-1 rounded-xl text-xs font-black uppercase tracking-widest border ${STATUS_CONFIG[filter].bg} ${STATUS_CONFIG[filter].color} ${STATUS_CONFIG[filter].border}`}>
                {STATUS_CONFIG[filter].label}
              </div>
              <span className="text-slate-500 text-sm">{filtered.length} dossier{filtered.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/50 w-52"
                />
              </div>
              <button
                onClick={loadUsers}
                className="p-2 rounded-xl bg-slate-900 border border-white/10 hover:border-indigo-500/30 text-slate-400 hover:text-white transition-all"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-white/5">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={32} className="text-indigo-400 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-500 space-y-4">
                <div className="p-6 rounded-full bg-white/5">
                  <Shield size={40} className="opacity-30" />
                </div>
                <p className="font-bold">Aucun dossier {STATUS_CONFIG[filter].label.toLowerCase()}</p>
              </div>
            ) : (
              filtered.map((user) => (
                <div key={user.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 hover:bg-white/3 transition-all">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/30 to-violet-500/30 border border-indigo-500/20 flex items-center justify-center font-black text-indigo-300 text-lg flex-shrink-0">
                      {user.username?.charAt(0).toUpperCase()}
                    </div>
                    <div className="space-y-1">
                      <p className="font-black text-white">{user.username}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Mail size={10} />{user.email}</span>
                        {user.country && <span className="flex items-center gap-1"><Globe size={10} />{user.country}</span>}
                        <span className="flex items-center gap-1"><Calendar size={10} />Inscrit le {formatDate(user.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 border border-white/10 hover:border-indigo-500/30 text-slate-400 hover:text-white text-xs font-bold transition-all"
                    >
                      <Eye size={12} /> Détails
                    </button>
                    {filter === 'pending' && (
                      <>
                        <button
                          onClick={() => handleVerify(user.id, 'verified')}
                          disabled={processingId === user.id}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500 hover:text-white text-xs font-bold transition-all disabled:opacity-50"
                        >
                          {processingId === user.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                          Approuver
                        </button>
                        <button
                          onClick={() => handleVerify(user.id, 'rejected')}
                          disabled={processingId === user.id}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 hover:bg-rose-500 hover:text-white text-xs font-bold transition-all disabled:opacity-50"
                        >
                          <XCircle size={12} /> Rejeter
                        </button>
                      </>
                    )}
                    {filter === 'rejected' && (
                      <button
                        onClick={() => handleVerify(user.id, 'verified')}
                        disabled={processingId === user.id}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500 hover:text-white text-xs font-bold transition-all disabled:opacity-50"
                      >
                        <CheckCircle size={12} /> Approuver quand même
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
